import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Select } from '../Select/Select';
import { Modal } from '../Modal/Modal';

export interface AddGapModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
}

const fieldLabel = 'mb-1.5 block text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--ink-3)]';
const readonlyField =
  'w-full rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3 py-[10px] text-[13.5px] text-[var(--ink-1)]';

function StartOption({
  active,
  title,
  sub,
  onClick,
}: {
  active: boolean;
  title: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 rounded-[var(--r-md)] border px-3 py-2.5 text-left transition',
        active
          ? 'border-[var(--ai-3)] bg-[color-mix(in_srgb,var(--ai-3)_8%,transparent)]'
          : 'border-[var(--cream-2)] bg-[var(--cream-1)]',
      )}
    >
      <span
        className={cn(
          'block text-[12.5px] font-semibold',
          active ? 'text-[var(--ai-3)]' : 'text-[var(--ink-0)]',
        )}
      >
        {title}
      </span>
      <span className="mt-0.5 block text-[11px] text-[var(--ink-4)]">{sub}</span>
    </button>
  );
}

/**
 * Add-a-gap modal (spec 05 §9) — for a chair that has come free but the schedule
 * does not know about yet. Length is calculated, never typed (§9). No live
 * eligibility count in V1 (§9.1) — one line sets the expectation instead.
 */
export function AddGapModal({ open, onClose, onSubmit }: AddGapModalProps) {
  const [why, setWhy] = useState('Cancelled or moved');
  const [fill, setFill] = useState('Hygiene-compatible — cleaning, exam, perio maintenance');
  const [start, setStart] = useState<'now' | 'hold'>('now');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a gap"
      className="w-[min(560px,calc(100vw-32px))]"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={onSubmit}>
            Add the gap
          </Button>
        </>
      }
    >
      <p className="m-0 mb-4 text-[12.5px] text-[var(--ink-3)]">
        For a chair that has come free but the schedule does not know about yet.
      </p>

      {/* provider / operatory / date */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <span className={fieldLabel}>Provider</span>
          <div className={readonlyField}>Dr. Patel</div>
        </div>
        <div>
          <span className={fieldLabel}>Operatory</span>
          <div className={readonlyField}>Op 3</div>
        </div>
        <div>
          <span className={fieldLabel}>Date</span>
          <div className={readonlyField}>Today · Thu 25 Sep</div>
        </div>
      </div>

      {/* from / to / length (calculated) */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div>
          <span className={fieldLabel}>From</span>
          <div className={readonlyField}>2:00 PM</div>
        </div>
        <div>
          <span className={fieldLabel}>To</span>
          <div className={readonlyField}>3:00 PM</div>
        </div>
        <div>
          <span className={fieldLabel}>Length</span>
          <div className={cn(readonlyField, 'flex items-center justify-between')}>
            <span>60 min</span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-4)]">
              Calculated
            </span>
          </div>
        </div>
      </div>

      {/* why is it free — feeds section 01 */}
      <div className="mt-3">
        <Select
          label="Why is it free"
          options={['Cancelled or moved', 'No-show', 'Short gap', 'Never booked', 'Added by staff']}
          value={why}
          onChange={setWhy}
        />
      </div>

      {/* what can fill it — decides eligibility */}
      <div className="mt-3">
        <Select
          label="What can fill it"
          options={[
            'Hygiene-compatible — cleaning, exam, perio maintenance',
            'Restorative — fillings, crown prep',
            'Hygiene only',
            'New patient exam',
          ]}
          value={fill}
          onChange={setFill}
        />
        <p className="mt-1.5 text-[11px] text-[var(--ink-4)]">
          Decides who is eligible. Narrower means fewer candidates and a better match — ELVA works
          out who qualifies once the gap is added, and the count appears on its card.
        </p>
      </div>

      {/* offer expires */}
      <div className="mt-3">
        <span className={fieldLabel}>Offer expires</span>
        <div className={cn(readonlyField, 'flex items-center justify-between')}>
          <span>1:00 PM</span>
          <span className="text-[11px] text-[var(--ink-4)]">1 hour before the slot</span>
        </div>
        <p className="mt-1.5 text-[11px] text-[var(--ink-4)]">
          After this, the gap closes on its own and nobody else is contacted.
        </p>
      </div>

      {/* start */}
      <div className="mt-3">
        <span className={fieldLabel}>Start</span>
        <div className="flex gap-2.5">
          <StartOption
            active={start === 'now'}
            title="Start filling now"
            sub="First batch goes out within a minute"
            onClick={() => {
              setStart('now');
            }}
          />
          <StartOption
            active={start === 'hold'}
            title="Hold until I resume"
            sub="Created and ranked, but nothing sent"
            onClick={() => {
              setStart('hold');
            }}
          />
        </div>
      </div>

      {/* short-notice behaviour note (§9.2) */}
      <p className="mt-3 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_30%,transparent)] bg-[var(--gold-tint)] px-3 py-2 text-[11.5px] text-[var(--ink-2)]">
        This slot is <span className="font-semibold text-[var(--gold)]">3 hours away</span>. The
        agent will text and call straight away rather than working in batches — there is not enough
        time for waves.
      </p>

      {/* footer reassurance (§9.3) */}
      <p className="mt-2 text-[11px] text-[var(--ink-4)]">
        A gap added here behaves exactly like one ELVA found — same rules, same cap, same log.
      </p>
    </Modal>
  );
}
